import type RouteInfo from '@ember/routing/route-info';
import type Transition from '@ember/routing/transition';
import Service, { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { ownerForPath, type RouteManifest } from 'lumen/utils/route-manifest';

export default class RouteManifestService extends Service {
  @service declare errorTracking: Services['error-tracking'];
  @service declare router: Services['router'];

  @tracked manifest: RouteManifest | null = null;

  handleTransition(transition: Transition): void {
    const { manifest } = this;
    if (!manifest || !transition.to) {
      return;
    }

    const url = this.urlForTransition(transition.to);
    if (url === undefined) {
      return;
    }

    const targetApp = ownerForPath(manifest, url);
    if (targetApp !== undefined && targetApp !== 'lumen') {
      transition.abort();
      this.handOff(url, isReplaceTransition(transition));
    }
  }

  handOff(url: string, replace: boolean): void {
    if (replace) {
      window.location.replace(url);
    } else {
      window.location.assign(url);
    }
  }

  async load(): Promise<void> {
    try {
      // nginx serves the generated manifest at this path in deployed
      // environments. Local dev has no nginx, so the same path is backed by a
      // committed copy in `public/` that you can edit to flip a route between
      // lumen and photon.
      const response = await fetch('/route-manifest.json');
      if (!response.ok) {
        throw new Error(`Manifest fetch failed (${response.status})`);
      }
      this.manifest = (await response.json()) as RouteManifest;
    } catch (error) {
      this.manifest = null;
      this.errorTracking.captureException(error);
    }
  }

  private urlForTransition(to: RouteInfo): string | undefined {
    const chain: RouteInfo[] = [];
    for (let info: RouteInfo | null = to; info; info = info.parent) {
      chain.unshift(info);
    }
    const models = chain.flatMap((info) =>
      info.paramNames.map((name) => info.params?.[name]),
    );
    try {
      return this.router.urlFor(to.name, ...models, {
        queryParams: to.queryParams,
      });
    } catch {
      return undefined;
    }
  }
}

function isReplaceTransition(transition: Transition): boolean {
  // `urlMethod` is part of router_js's public Transition type ('update' |
  // 'replace' | null), re-exported by Ember:
  // https://github.com/tildeio/router.js/blob/master/lib/router/transition.ts
  return transition.urlMethod === 'replace';
}

declare module '@ember/service' {
  interface Registry {
    'route-manifest': RouteManifestService;
  }
}
